<template>
  <BaseSideModal :width="'960px'" @close="$emit('close')">
    <template #title>
      <span>{{ $t('carriers.import.diff.Title') }}</span>
    </template>

    <!-- In the body, not the title slot: BaseSideModal sizes its scroll area as
         calc(100% - 172px), a hard-coded one-line header. -->
    <div class="policy-diff__bar">
      <BaseButton
        type="outline"
        is-small
        :disabled="index === 0 || loading"
        @click="step(-1)"
      >←</BaseButton>
      <span class="policy-diff__position">
        {{ $t('carriers.import.diff.Position', { n: index + 1, total: queue.length }) }}
      </span>
      <BaseButton
        type="outline"
        is-small
        :disabled="index >= queue.length - 1 || loading"
        @click="step(1)"
      >→</BaseButton>
      <span class="policy-diff__who">
        {{ current.policyNumber }} · {{ current.clientName }}
      </span>
    </div>

    <BasePreloader v-if="loading" color="purple" size="l"/>

    <p v-else-if="!current.id" class="policy-diff__same">
      {{ $t('carriers.import.snapshot.Empty') }}
    </p>

    <template v-else>
      <p v-if="scheduleWouldEmpty" class="policy-diff__warning">
        {{ $t('carriers.import.diff.ScheduleWouldEmpty') }}
      </p>

      <!-- Not when what moved is only hidden people rows (group members). -->
      <p v-if="!changedCount && !hiddenChanged" class="policy-diff__same">
        {{ $t('carriers.import.snapshot.NothingMoved') }}
      </p>

      <table class="policy-diff__table">
        <thead>
          <tr>
            <th>{{ $t('carriers.import.snapshot.Field') }}</th>
            <th>{{ $t('carriers.import.snapshot.InRenova') }}</th>
            <th>{{ $t('carriers.import.snapshot.AtTheCarrier') }}</th>
          </tr>
        </thead>
        <tbody>
          <!-- Unchanged rows stay, receding. "The carrier agrees with us on
               everything except the balance" is the answer a reader most needs,
               and a list of only the changes cannot give it. -->
          <template v-for="row in shownChanges">
            <!-- The carrier's services, one box each, in place of the plain row
                 while this one is still the person's to apply. -->
            <tr v-if="row.field === 'coverages' && showPicker" :key="row.field">
              <td>{{ $t('adminTitles.services') }}</td>
              <td colspan="2" class="policy-diff__services">
                <ServicePicker v-model="pickedServices" :items="serviceItems" :disabled="Boolean(busy)"/>
              </td>
            </tr>
            <tr
              v-else
              :key="row.field"
              :class="row.changed ? 'policy-diff__row--changed' : 'policy-diff__row--same'"
            >
              <td>{{ fieldLabel(row.field) }}</td>
              <td>
                <span :class="{ 'run-diff--was': row.changed }">
                  {{ format(row, 'current') }}
                </span>
              </td>
              <td>
                <span :class="{ 'run-diff--now': row.changed }">
                  {{ format(row, 'incoming') }}
                </span>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <!-- Services with no row of their own in the diff still get their boxes. -->
      <section v-if="showPicker && !hasCoverageRow" class="policy-diff__services-block">
        <p class="policy-diff__services-title">{{ $t('adminTitles.services') }}</p>
        <ServicePicker v-model="pickedServices" :items="serviceItems" :disabled="Boolean(busy)"/>
      </section>

      <p v-if="failure" class="policy-diff__failure">{{ failure }}</p>
    </template>

    <template #footer>
      <p class="policy-diff__note">
        <template v-if="current.reviewStatus && current.reviewStatus !== 'PENDING'">
          {{ statusText(current.reviewStatus) }}
        </template>
        <template v-else>{{ $t('carriers.import.diff.Note') }}</template>
      </p>
      <BaseButton type="outline" @click="$emit('close')">{{ $t('buttons.back') }}</BaseButton>
      <!-- Only while the row is still the person's to decide: applying one the
           server already resolved fails, and offering it promises otherwise. -->
      <template v-if="canApply && current.reviewStatus === 'PENDING'">
        <BaseButton
          type="outline"
          :disabled="busy || loading"
          :loading="busy === 'dismiss'"
          @click="resolve('dismiss')"
        >{{ $t('carriers.import.snapshot.Dismiss') }}</BaseButton>
        <BaseButton
          :disabled="busy || loading"
          :loading="busy === 'apply'"
          @click="resolve('apply')"
        >{{ $t('carriers.import.diff.ApplyThisOne') }}</BaseButton>
      </template>
    </template>
  </BaseSideModal>
</template>

<script>
import BaseSideModal from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/BaseSideModal';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
import { UserPermissions } from '@/utils/permissions';
import { apiError } from '@/utils/carrierImport';
import {
  diffFieldLabel, formatDiffSide, isShownDiffField, pickedServiceNames, serviceItems,
} from '@/utils/carrierFormat';
import ServicePicker from './ServicePicker';

/**
 * One policy the carrier disagrees with us about, in full.
 *
 * One at a time rather than a list of expandable rows: deciding whether to take
 * the carrier's figures is reading, not scanning, and a run of 116 is walkable
 * with next-previous without going back to the queue between each one. Applying
 * advances rather than closing, so a queue can be worked through in a pass.
 *
 * The diff is fetched per policy and **recomputed server-side against the book
 * as it is now**, not replayed from what staging recorded. Weeks can pass before
 * somebody opens one of these, including the case that matters most — an
 * operator has already made the edit the carrier is proposing.
 */
export default {
  name: 'SnapshotPolicyDiffModal',
  components: {
    BaseSideModal, BaseButton, BasePreloader, ServicePicker,
  },
  mixins: [UserPermissions],
  props: {
    /** The pending UPDATE rows of this run, in list order. */
    rows: { type: Array, required: true },
    /** Where to start — the row that was clicked. */
    startIndex: { type: Number, default: 0 },
    carrierName: { type: String, default: '' },
  },
  emits: ['close', 'resolved'],
  data() {
    return {
      // A copy, because working through the queue removes the rows that get
      // decided and the list behind us owns its own array.
      queue: this.rows.slice(),
      index: this.startIndex,
      changes: [],
      scheduleWouldEmpty: false,
      /**
       * The carrier's services against the policy's own: {add, update, remove}
       * names and, from a newer backend, `items` with the detail. Null when the
       * backend has nothing to say about them.
       */
      services: null,
      /** The services ticked to apply. None by default, and none again for every row. */
      pickedServices: [],
      loading: true,
      busy: null,
      failure: '',
    };
  },
  computed: {
    canApply() {
      return this.checkUserPermissions([this.PermissionsTypes.POLICIES_UPDATE]);
    },
    current() {
      return this.queue[this.index] || {};
    },
    /** Group members still sync, but as counts they told a reviewer nothing. */
    shownChanges() {
      return this.changes.filter((c) => isShownDiffField(c.field));
    },
    changedCount() {
      return this.shownChanges.filter((c) => c.changed).length;
    },
    hiddenChanged() {
      return this.changes.some((c) => c.changed && !isShownDiffField(c.field));
    },
    hasCoverageRow() {
      return this.shownChanges.some((c) => c.field === 'coverages');
    },
    serviceItems() {
      return serviceItems(this.services);
    },
    /** Only where a tick can take effect: there are services, and Apply is on offer. */
    showPicker() {
      return this.serviceItems.length > 0 && this.canApply && this.current.reviewStatus === 'PENDING';
    },
  },
  created() {
    this.load();
  },
  methods: {
    format(row, side) {
      return formatDiffSide(row, side, this.$t.bind(this), this.$tc.bind(this));
    },
    /** Guarded: the enum carries APPLYING, which has no copy of its own. */
    statusText(status) {
      const key = `carriers.import.snapshot.status.${status}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    /** A field nobody has named yet shows its key rather than a blank cell. */
    fieldLabel(field) {
      return diffFieldLabel(field, this.$t.bind(this), this.$te.bind(this));
    },
    load() {
      this.failure = '';
      // A tick is a decision about one policy; it never carries over to the next.
      this.pickedServices = [];
      this.services = null;
      // Nothing to fetch is an answer, not a wait. This returned with `loading`
      // left true, which rendered a preloader that could never stop.
      if (!this.current.id) { this.loading = false; return Promise.resolve(); }
      this.loading = true;
      return this.$store.dispatch('carrierImportFetchSnapshotDetail', this.current.id)
        .then((data) => {
          this.changes = (data && data.changes) || [];
          this.scheduleWouldEmpty = Boolean(data && data.scheduleWouldEmpty);
          this.services = (data && data.services) || null;
        })
        .catch((err) => this.$notify.error({
          message: apiError(err, this.$t('adminTitles.someError')),
        }))
        .finally(() => { this.loading = false; });
    },
    step(by) {
      const next = this.index + by;
      if (next < 0 || next >= this.queue.length) return;
      this.index = next;
      this.load();
    },
    /**
     * Decide about this one, then move on.
     *
     * The decided row is dropped from the local queue rather than re-fetched:
     * the list behind refreshes on `resolved`, and re-numbering under the
     * person's cursor mid-pass is worse than a slightly stale count.
     */
    resolve(what) {
      const {id} = this.current;
      if (!id) return;
      this.busy = what;
      this.failure = '';
      const action = what === 'apply'
        ? 'carrierImportApplySnapshot'
        : 'carrierImportDismissSnapshot';
      // The ticked services ride only on this one-row apply; the list's bulk
      // apply never sends any.
      const names = what === 'apply' && this.showPicker
        ? pickedServiceNames(this.serviceItems, this.pickedServices)
        : [];
      const payload = names.length ? { ids: [id], services: [{ id, names }] } : [id];
      this.$store.dispatch(action, payload)
        .then((res) => {
          const ok = what === 'apply'
            ? (res && res.appliedCount)
            : (res && res.dismissedCount);
          // A read nobody pressed Read for: the accept queued it so the policy just
          // added gets its receipts. Said, so it is no surprise under All updates.
          if (res && res.paymentsReads && res.paymentsReads.length) {
            this.$notify.success({
              message: this.$t('carriers.import.snapshot.PaymentsReadQueued', {
                carrier: this.carrierName || this.$t('carriers.import.TheCarrier'),
              }),
            });
          }
          this.$emit('resolved');
          if (!ok) {
            // It stays PENDING server-side, so it stays here with its reason
            // rather than being skipped past as though it were done.
            this.failure = this.$t('carriers.import.diff.CouldNotApply');
            return null;
          }
          this.queue.splice(this.index, 1);
          if (!this.queue.length) { this.$emit('close'); return null; }
          if (this.index >= this.queue.length) this.index = this.queue.length - 1;
          return this.load();
        })
        .catch((err) => this.$notify.error({
          message: apiError(err, this.$t('adminTitles.someError')),
        }))
        .finally(() => { this.busy = null; });
    },
  },
};
</script>

<style lang="scss" scoped>
.policy-diff {
  &__bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 12px;
    margin-bottom: 12px;
    border-bottom: 1px solid var(--solid-lavender-grey);
  }

  &__position {
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  &__who {
    margin-left: auto;
    font-size: 13px;
    font-weight: 600;
    color: var(--solid-black-purple);
    min-width: 0;
    overflow-wrap: anywhere;
  }

  &__warning {
    background: var(--solid-milk-ornage);
    border-left: 4px solid var(--solid-orange);
    padding: 12px 16px;
    border-radius: 4px;
    font-size: 13px;
    line-height: 1.5;
    color: var(--solid-black-purple-70);
    margin-bottom: 12px;
  }

  &__same {
    font-size: 13px;
    color: var(--solid-dark-purple);
    margin-bottom: 12px;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-variant-numeric: tabular-nums;

    th,
    td {
      text-align: left;
      padding: 7px 8px;
      border-bottom: 1px solid var(--solid-lavender-grey);
      vertical-align: top;
      overflow-wrap: anywhere;
      white-space: pre-line;
    }

    th {
      color: var(--solid-dark-purple);
      font-weight: 600;
      font-size: 12.5px;
    }

    td { font-size: 13px; }

    // The picker lays itself out; pre-line would print the template's own line breaks.
    td.policy-diff__services { white-space: normal; }
  }

  // The rows that would actually move, findable in a table of twenty.
  &__row--changed { background: var(--solid-light-purple); }

  // Everything the carrier agrees with us about is context, and recedes.
  &__row--same {
    color: var(--solid-dark-purple);
    opacity: 0.65;
  }

  &__services-block {
    margin-top: 16px;
  }

  &__services-title {
    font-size: 12.5px;
    font-weight: 600;
    color: var(--solid-dark-purple);
    margin-bottom: 6px;
  }

  &__failure {
    font-size: 12.5px;
    color: var(--solid-red);
    margin-top: 12px;
  }

  &__note {
    margin-right: auto;
    font-size: 12.5px;
    line-height: 1.4;
    color: var(--solid-dark-purple);
  }
}
</style>
